import {Injectable, OnDestroy} from '@angular/core';
import {Params, Router} from '@angular/router';
import {
  AppContainerOptions,
  ContentSideNavButton,
  LeftPaneContent,
  LinkSideNavButton,
  SideNavBarService as ApSideNavBarService,
  SideNavButton, SideNavButtonAlignment
} from '@mydocma/app-container';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';
import {BpRoutingService} from '../bp-routing/bp-routing.service';
import {StructureLeftPaneComponent, BpViewService} from '@mydocma/co/src/lib/common';
import {SideNavBarButtonsEnum} from './sidenavbar-buttons.enum';
import {BpProjectPermissions} from '../bp-project/bp-project-permissions';
import {CoPermissionArea, CoPermissionsService, CoPermissionType} from '../co-permissions.service';
import {Project} from "@mydocma/project";


@UntilDestroy()
@Injectable()
export class SideNavBarService implements OnDestroy {

  private readonly idToButtonMap = new Map<string, SideNavButton>([
    [SideNavBarButtonsEnum.ViewGroup, new ContentSideNavButton({
      id: SideNavBarButtonsEnum.ViewGroup,
      icon: 'visibility',
      expanded: true
    })],
    [SideNavBarButtonsEnum.ViewStructure, new ContentSideNavButton({
      id: SideNavBarButtonsEnum.ViewStructure,
      parentId: SideNavBarButtonsEnum.ViewGroup,
      icon: 'account_tree',
      active: true,
      content: new LeftPaneContent({
        id: 'structure',
        componetType: StructureLeftPaneComponent,
      })
    })],
    [SideNavBarButtonsEnum.Customer, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Customer,
      icon: '_database-administrator',
      alignment: SideNavButtonAlignment.bottom,hidden: true
    })],
    [SideNavBarButtonsEnum.Structures, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Structures,
      icon: '_stacked-organizational-chart',
    })],
    [SideNavBarButtonsEnum.BimModels, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.BimModels,
      icon: '_bim-model',
    })],
    [SideNavBarButtonsEnum.Sections, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Sections,
      icon: '_bim-section',
    })],
    [SideNavBarButtonsEnum.Plans, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Plans,
      icon: '_plan_without_pin',
    })],
    [SideNavBarButtonsEnum.Missions, new LinkSideNavButton({
      id: SideNavBarButtonsEnum.Missions,
      icon: '_journey',
    })]
  ]);

  private idToUrlMap = new Map<string, string>([
    [SideNavBarButtonsEnum.BimModels, '/projects/:projectId/bim-models'],
    [SideNavBarButtonsEnum.Sections, '/projects/:projectId/sections'],
    [SideNavBarButtonsEnum.Plans, '/projects/:projectId/plans'],
    [SideNavBarButtonsEnum.Structures, '/projects/:projectId/structures'],
    [SideNavBarButtonsEnum.Missions, '/missions'],
    [SideNavBarButtonsEnum.Customer, '/customers'],
  ]);

  private readonly idToTranslationKey = new Map<string, string>([
    [SideNavBarButtonsEnum.ViewGroup, 'CO.sidebar.view'],
    [SideNavBarButtonsEnum.ViewStructure, 'CO.sidebar.structure'],
    [SideNavBarButtonsEnum.BimModels, 'CO.sidebar.bimModels'],
    [SideNavBarButtonsEnum.Sections, 'CO.sidebar.sections'],
    [SideNavBarButtonsEnum.Plans, 'CO.sidebar.plans'],
    [SideNavBarButtonsEnum.Structures, 'CO.sidebar.structures'],
    [SideNavBarButtonsEnum.Missions, 'CO.sidebar.routes'],
    [SideNavBarButtonsEnum.Customer, 'CO.sidebar.globalConfiguration'],
  ]);
  private buttons: SideNavButton[] = [];

  private lastRouteParams: Params;
  private lastRoute: string;


  constructor(
    private readonly translateService: TranslateService,
    private readonly routingService: BpRoutingService,
    private readonly bpViewService: BpViewService,
    private readonly router: Router,
    private readonly bpPermissionService: CoPermissionsService
  ) {

    this.bpViewService.currentProject$
      .pipe(untilDestroyed(this))
      .subscribe(project => {
        this.initButtons(project);
      });

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(l => this.transalteButtons());


    this.routingService.moduleActivatedRoute$
      .pipe(
        untilDestroyed(this)
      ).subscribe(async (mr) => {
      if (mr) {
        const p = this.routingService.getRoutePath(mr.route.snapshot);
        this.lastRouteParams = mr.params;
        this.lastRoute = p;
        this.onRouteChanged(p);
      }
    });

    this.bpPermissionService.schemaGlobalConfiguration$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.updateCustomerButtonVisibility());
  }

  ngOnDestroy(): void {
  }

  register(appContainer: AppContainerOptions) {
    appContainer.sideNavBar.buttons = this.buttons;
  }

  unregister(appContainer: AppContainerOptions) {
    appContainer.sideNavBar.buttons = [];
  }

  private async onRouteChanged(route: string) {
    for (let b of this.buttons) {
      if (b instanceof LinkSideNavButton) {
        const buttonPath = this.idToUrlMap.get(b.id);
        b.active = buttonPath === route;
      }
    }
  }

  private async initButtons(project: any) {
    this.transalteButtons();
    if (this.buttons.length === 0) {
      for (const btn of this.idToButtonMap.values()) {
        if (BpProjectPermissions.includes(btn.id)) {
          btn.hidden =  !await this.checkButtonVisibility(btn);
          const buttonPath = this.idToUrlMap.get(btn.id);
          btn.active = buttonPath === this.lastRoute;
        }
        if(btn.id === SideNavBarButtonsEnum.Customer){
          // visible only if global configuration is enabled
          btn.hidden = !this.bpPermissionService.isGlobalConfigurationEnabled();
        }
        this.buttons.push(btn);
        if (btn instanceof LinkSideNavButton) {
          btn.click.subscribe(b => this.onLinkButtonClick(btn.id));
        }
      }

    } else {
      for (const btn of this.buttons) {
        if (BpProjectPermissions.includes(btn.id)) {
          btn.hidden = !await this.checkButtonVisibility(btn);
        }
      }
      this.updateCustomerButtonVisibility();
    }
  }

  private transalteButtons() {
    this.idToTranslationKey.forEach((key, id) => {
      const btn = this.idToButtonMap.get(id);
      if (btn && key) {
        btn.text = this.translateService.instant(key);
        btn.tooltip = this.translateService.instant(key);
      }
    });
  }

  private async onLinkButtonClick(id: string) {
    let url = this.idToUrlMap.get(id);
    if (!url) {
      return;
    }

    if (id === SideNavBarButtonsEnum.Customer) {
      this.router.navigateByUrl(url);
      return;
    }

    if (this.lastRouteParams?.projectId) {
      url = url.replace(':projectId', this.lastRouteParams.projectId);
    }
    this.routingService.navigate([url]);
  }


  private async checkButtonVisibility(btn: SideNavButton): Promise<boolean> {
    return this.bpPermissionService.hasProjectPermission(btn.id as CoPermissionArea, CoPermissionType.Visible);

  }

  private updateCustomerButtonVisibility(): void {
    const customerBtn = this.buttons.find(b => b instanceof LinkSideNavButton && b.id === SideNavBarButtonsEnum.Customer);
    if (!customerBtn) { return; }
    customerBtn.hidden = !this.bpPermissionService.isGlobalConfigurationEnabled();
  }

}
